import type { ReactNode } from 'react'

import { AuthErrorBanner } from '@/customer/components/auth/auth-error-banner'
import { OtpInput, useOtpFlow } from '@/customer/components/auth/otp-input'

/**
 * Packaged "verify-your-identifier" layout: `useOtpFlow` state + the
 * shared chrome (blurb slot, resend button, OTP input, error banner,
 * optional extras slot).
 *
 * Reach for `useOtpFlow` + `<OtpInput>` directly when the surrounding
 * layout needs to differ (e.g. sign-in's method-picker page). Use
 * `<OtpPanel>` for the standard "verify or change my identifier"
 * account flows.
 */
export function OtpPanel({
  sendCode,
  verifyCode,
  onSuccess,
  autoSend,
  blurb,
  extras,
}: {
  sendCode: () => Promise<void>
  verifyCode: (code: string) => Promise<void>
  onSuccess: () => void
  autoSend?: boolean
  blurb: ReactNode
  extras?: ReactNode
}) {
  const otp = useOtpFlow({ sendCode, verifyCode, onSuccess, autoSend })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        {blurb}
        <div className="mt-1.5 flex items-center gap-3 text-label-sm text-muted-foreground">
          <span>Didn&apos;t get the code?</span>
          <button
            type="button"
            onClick={otp.resend}
            disabled={otp.secondsRemaining > 0}
            className="cursor-pointer font-semibold text-foreground underline underline-offset-4 disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
          >
            {otp.secondsRemaining > 0 ? `Resend in ${otp.secondsRemaining}s` : 'Resend'}
          </button>
        </div>
        {extras}
      </div>

      <OtpInput value={otp.otpCode} onChange={otp.setOtpCode} disabled={otp.isVerifying} />

      <AuthErrorBanner code={otp.errorCode} />
    </div>
  )
}
